import { createSignal } from 'solid-js';
import { clearApiCache } from '../api/client';

// Bumped when the app returns to the foreground after a long enough absence.
// `AppRoot` keys the <Router> on this, so a bump remounts the whole route tree:
// the current URL is re-matched and every page re-runs its createResource
// against a freshly-cleared API cache — newly published albums appear without a
// manual reload. See client.ts for why the plain in-memory getCache would
// otherwise keep serving stale data for the lifetime of an installed PWA.
const [refreshKey, setRefreshKey] = createSignal(1);
export { refreshKey };

// Only refresh after a genuine break. A quick switch away (glancing at a
// notification, answering a call) shouldn't blow away what the user was looking
// at mid-scroll.
const STALE_AFTER_MS = 3 * 60_000;

// When the page went hidden, or null while visible. Guarded so the *earliest*
// hidden time wins when several "leaving" events fire for one backgrounding.
let hiddenAt: number | null = null;

function markHidden(): void {
    if (hiddenAt === null) hiddenAt = Date.now();
}

function maybeRefresh(): void {
    if (hiddenAt === null) return;
    const away = Date.now() - hiddenAt;
    hiddenAt = null;
    if (away < STALE_AFTER_MS) return;
    clearApiCache();
    setRefreshKey((n) => n + 1);
}

/**
 * Auto-refresh when returning to the app. Once the page has been hidden (app
 * backgrounded, screen off, tab switched) for at least STALE_AFTER_MS and then
 * becomes visible again, drop the session GET cache and remount the route tree.
 *
 * Mobile browsers don't reliably fire `visibilitychange` when an installed PWA
 * is backgrounded, so we also listen for `pagehide` (the dependable "leaving"
 * signal) and `pageshow` (which fires on bfcache restore). All paths funnel
 * through the same guarded markHidden/maybeRefresh pair, so a single
 * background→foreground cycle triggers at most one refresh.
 */
export function installAutoRefresh(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') markHidden();
        else maybeRefresh();
    });
    window.addEventListener('pagehide', markHidden);
    window.addEventListener('pageshow', maybeRefresh);
}
